import React, { useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Upload, X, Image as ImageIcon } from "lucide-react";

export const POSITIONS = ["RHP", "LHP", "C", "1B", "2B", "3B", "SS", "LF", "CF", "RF", "OF", "UTIL", "DH"];
export const PROVINCES = ["AB", "BC", "MB", "NB", "NL", "NS", "NT", "NU", "ON", "PE", "QC", "SK", "YT"];

const inputCls = "w-full px-4 py-3 border border-slate-300 rounded-sm text-sm focus:outline-none focus:border-[#0A192F]";

function Field({ label, required, children }) {
  return (
    <div>
      <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
        {label}{required && <span className="text-[#E63946]"> *</span>}
      </label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

// Reusable player-profile fieldset for the interest + free-profile forms.
// `f` is a flat state object; `set(field, value)` updates it.
export default function ProfileFieldset({ f, set, disableEmail = false, testidPrefix = "profile" }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [uploadErr, setUploadErr] = useState("");

  const handlePhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadErr("");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/interests/upload-photo", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      set("profile_photo_url", data.url);
    } catch (err) {
      setUploadErr(formatApiError(err?.response?.data?.detail) || "Upload failed. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-8">
      {/* Identity */}
      <section>
        <div className="text-xs font-semibold uppercase tracking-wider text-[#E63946] mb-4">Player Information</div>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="First Name" required>
            <input data-testid={`${testidPrefix}-first-name`} className={inputCls} value={f.first_name || ""} onChange={(e) => set("first_name", e.target.value)} />
          </Field>
          <Field label="Last Name" required>
            <input data-testid={`${testidPrefix}-last-name`} className={inputCls} value={f.last_name || ""} onChange={(e) => set("last_name", e.target.value)} />
          </Field>
          <Field label="Player Email" required>
            <input data-testid={`${testidPrefix}-email`} type="email" disabled={disableEmail} className={`${inputCls} ${disableEmail ? "bg-slate-100 text-slate-500" : ""}`} value={f.email || ""} onChange={(e) => set("email", e.target.value)} />
          </Field>
          <Field label="Player Cell">
            <input data-testid={`${testidPrefix}-phone`} className={inputCls} value={f.phone || ""} onChange={(e) => set("phone", e.target.value)} />
          </Field>
          <Field label="Date of Birth" required>
            <input data-testid={`${testidPrefix}-dob`} type="date" className={inputCls} value={f.date_of_birth || ""} onChange={(e) => set("date_of_birth", e.target.value)} />
          </Field>
          <Field label="Graduation Year" required>
            <input data-testid={`${testidPrefix}-grad-year`} type="number" placeholder="e.g. 2028" className={inputCls} value={f.graduation_year || ""} onChange={(e) => set("graduation_year", e.target.value)} />
          </Field>
          <Field label="City">
            <input data-testid={`${testidPrefix}-city`} className={inputCls} value={f.city || ""} onChange={(e) => set("city", e.target.value)} />
          </Field>
          <Field label="Province" required>
            <select data-testid={`${testidPrefix}-province`} className={`${inputCls} bg-white`} value={f.province || ""} onChange={(e) => set("province", e.target.value)}>
              <option value="">Select…</option>
              {PROVINCES.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </Field>
        </div>
      </section>

      {/* Physical / Position */}
      <section>
        <div className="text-xs font-semibold uppercase tracking-wider text-[#E63946] mb-4">Position & Physicals</div>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Primary Position" required>
            <select data-testid={`${testidPrefix}-primary-position`} className={`${inputCls} bg-white`} value={f.primary_position || ""} onChange={(e) => set("primary_position", e.target.value)}>
              <option value="">Select…</option>
              {POSITIONS.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </Field>
          <Field label="Secondary Position">
            <select data-testid={`${testidPrefix}-secondary-position`} className={`${inputCls} bg-white`} value={f.secondary_position || ""} onChange={(e) => set("secondary_position", e.target.value)}>
              <option value="">None</option>
              {POSITIONS.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </Field>
          <Field label="Height">
            <div className="flex gap-2">
              <select data-testid={`${testidPrefix}-height-ft`} className={`${inputCls} bg-white`} value={f.height_ft || ""} onChange={(e) => set("height_ft", e.target.value)}>
                <option value="">ft</option>
                {[4, 5, 6, 7].map((v) => <option key={v} value={v}>{v} ft</option>)}
              </select>
              <select data-testid={`${testidPrefix}-height-in`} className={`${inputCls} bg-white`} value={f.height_in ?? ""} onChange={(e) => set("height_in", e.target.value)}>
                <option value="">in</option>
                {[...Array(12).keys()].map((v) => <option key={v} value={v}>{v} in</option>)}
              </select>
            </div>
          </Field>
          <Field label="Weight (lbs)">
            <input data-testid={`${testidPrefix}-weight`} type="number" className={inputCls} value={f.weight_lbs || ""} onChange={(e) => set("weight_lbs", e.target.value)} />
          </Field>
          <Field label="Bats">
            <select data-testid={`${testidPrefix}-bats`} className={`${inputCls} bg-white`} value={f.bats || ""} onChange={(e) => set("bats", e.target.value)}>
              <option value="">Select…</option>
              <option value="R">Right</option><option value="L">Left</option><option value="S">Switch</option>
            </select>
          </Field>
          <Field label="Throws">
            <select data-testid={`${testidPrefix}-throws`} className={`${inputCls} bg-white`} value={f.throws || ""} onChange={(e) => set("throws", e.target.value)}>
              <option value="">Select…</option>
              <option value="R">Right</option><option value="L">Left</option>
            </select>
          </Field>
        </div>
      </section>

      {/* Team / School */}
      <section>
        <div className="text-xs font-semibold uppercase tracking-wider text-[#E63946] mb-4">Team & School</div>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Current Team">
            <input data-testid={`${testidPrefix}-current-team`} className={inputCls} value={f.current_team || ""} onChange={(e) => set("current_team", e.target.value)} />
          </Field>
          <Field label="Current League">
            <input data-testid={`${testidPrefix}-league`} className={inputCls} value={f.league || ""} onChange={(e) => set("league", e.target.value)} />
          </Field>
          <Field label="High School" required>
            <input data-testid={`${testidPrefix}-school`} className={inputCls} value={f.school || ""} onChange={(e) => set("school", e.target.value)} />
          </Field>
        </div>
      </section>

      {/* Parent / Guardian */}
      <section>
        <div className="text-xs font-semibold uppercase tracking-wider text-[#E63946] mb-4">Parent / Guardian</div>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Parent / Guardian Name">
            <input data-testid={`${testidPrefix}-parent-name`} className={inputCls} value={f.parent_name || ""} onChange={(e) => set("parent_name", e.target.value)} />
          </Field>
          <Field label="Parent Email">
            <input data-testid={`${testidPrefix}-parent-email`} type="email" className={inputCls} value={f.parent_email || ""} onChange={(e) => set("parent_email", e.target.value)} />
          </Field>
          <Field label="Parent Cell">
            <input data-testid={`${testidPrefix}-parent-phone`} className={inputCls} value={f.parent_phone || ""} onChange={(e) => set("parent_phone", e.target.value)} />
          </Field>
        </div>
      </section>

      {/* Social */}
      <section>
        <div className="text-xs font-semibold uppercase tracking-wider text-[#E63946] mb-4">Social</div>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Instagram">
            <input data-testid={`${testidPrefix}-instagram`} placeholder="@handle" className={inputCls} value={f.instagram_handle || ""} onChange={(e) => set("instagram_handle", e.target.value)} />
          </Field>
          <Field label="X (Twitter)">
            <input data-testid={`${testidPrefix}-x`} placeholder="@handle" className={inputCls} value={f.x_handle || ""} onChange={(e) => set("x_handle", e.target.value)} />
          </Field>
        </div>
      </section>

      {/* Media */}
      <section>
        <div className="text-xs font-semibold uppercase tracking-wider text-[#E63946] mb-4">Media</div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2">
            <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Profile Photo</label>
            <div className="mt-2 flex items-center gap-4">
              <div className="w-20 h-20 rounded-sm bg-slate-100 border border-slate-200 grid place-items-center overflow-hidden shrink-0">
                {f.profile_photo_url
                  ? <img src={f.profile_photo_url} alt="" className="w-full h-full object-cover" />
                  : <ImageIcon className="w-6 h-6 text-slate-300" />}
              </div>
              <div className="flex items-center gap-2">
                <input ref={fileRef} type="file" accept="image/*" className="hidden" data-testid={`${testidPrefix}-photo-input`} onChange={handlePhoto} />
                <button type="button" data-testid={`${testidPrefix}-photo-upload-btn`} onClick={() => fileRef.current?.click()} disabled={uploading}
                  className="inline-flex items-center gap-2 px-4 py-2 border border-slate-300 rounded-sm text-sm font-semibold uppercase tracking-wider hover:border-[#0A192F] disabled:opacity-60">
                  <Upload className="w-4 h-4" /> {uploading ? "Uploading…" : "Upload Photo"}
                </button>
                {f.profile_photo_url && (
                  <button type="button" data-testid={`${testidPrefix}-photo-remove-btn`} onClick={() => set("profile_photo_url", "")}
                    className="inline-flex items-center gap-1 px-3 py-2 text-sm text-slate-500 hover:text-[#E63946]">
                    <X className="w-4 h-4" /> Remove
                  </button>
                )}
              </div>
            </div>
            {uploadErr && <div className="text-xs text-[#E63946] mt-2" data-testid={`${testidPrefix}-photo-error`}>{uploadErr}</div>}
          </div>
          <Field label="Swing Video URL">
            <input data-testid={`${testidPrefix}-swing-video`} placeholder="YouTube / Hudl / etc." className={inputCls} value={f.swing_video_url || ""} onChange={(e) => set("swing_video_url", e.target.value)} />
          </Field>
          <Field label="Pitching Video URL">
            <input data-testid={`${testidPrefix}-pitching-video`} placeholder="YouTube / Hudl / etc." className={inputCls} value={f.pitching_video_url || ""} onChange={(e) => set("pitching_video_url", e.target.value)} />
          </Field>
          <Field label="Highlight Video URL">
            <input data-testid={`${testidPrefix}-highlight-video`} placeholder="YouTube / Hudl / etc." className={inputCls} value={f.highlight_video_url || ""} onChange={(e) => set("highlight_video_url", e.target.value)} />
          </Field>
        </div>
      </section>
    </div>
  );
}

// Split flat form state into the API payload shape. Converts height ft/in -> cm
// and weight lbs -> kg so the player profile is populated with canonical units.
export function buildProfilePayload(f) {
  const profile = {};
  const PROFILE_STRINGS = ["first_name", "last_name", "date_of_birth", "email", "phone", "city",
    "province", "school", "primary_position", "secondary_position", "bats", "throws",
    "current_team", "league", "parent_name", "parent_email", "parent_phone",
    "instagram_handle", "x_handle"];
  PROFILE_STRINGS.forEach((k) => { if (f[k]) profile[k] = f[k]; });
  if (f.graduation_year) profile.graduation_year = Number(f.graduation_year);
  const ft = Number(f.height_ft || 0), inch = Number(f.height_in || 0);
  if (ft) profile.height_cm = Math.round((ft * 12 + inch) * 2.54 * 10) / 10;
  if (f.weight_lbs) profile.weight_kg = Math.round(Number(f.weight_lbs) * 0.453592 * 10) / 10;

  const media = {};
  ["profile_photo_url", "swing_video_url", "pitching_video_url", "highlight_video_url"].forEach((k) => {
    if (f[k]) media[k] = f[k];
  });
  return { profile, media };
}
